import { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MangaCard from '@/components/feature/MangaCard';
import { mangas, categories } from '@/mocks/manga';

export default function Category() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const activeId = Number(id ?? 0);

  const activeCategory = categories.find((c) => c.id === activeId);

  const filtered = useMemo(() => {
    let list = mangas;
    if (activeId !== 0) list = list.filter((m) => m.categoryId === activeId);
    if (q.trim()) {
      const kw = q.trim().toLowerCase();
      list = list.filter(
        (m) =>
          m.title.toLowerCase().includes(kw) ||
          m.author.toLowerCase().includes(kw) ||
          m.description.toLowerCase().includes(kw),
      );
    }
    return list;
  }, [activeId, q]);

  const pageTitle = q.trim()
    ? `"${q.trim()}" 的搜索结果`
    : activeId === 0
      ? '全部漫画'
      : `${activeCategory?.name ?? ''}漫画`;

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://comicniche.github.io/' },
      { '@type': 'ListItem', position: 2, name: pageTitle, item: `https://comicniche.github.io/category/${activeId}` },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(categoryJsonLd) }} />
      <Navbar />

      <main className="mx-auto max-w-7xl px-4 pt-24 pb-10 md:px-6">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: pageTitle },
          ]}
        />

        <div className="mb-6">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{pageTitle}</h1>
          {q.trim() ? (
            <p className="mt-2 text-sm text-foreground-600">
              为你找到 {filtered.length} 部与「{q.trim()}」相关的日漫汉化作品
            </p>
          ) : (
            <p className="mt-2 text-sm text-foreground-600">
              {activeCategory?.desc ?? '浏览日漫屋全部汉化漫画资源'}
            </p>
          )}
        </div>

        {/* Category tabs */}
        <div className="mb-8 flex flex-wrap gap-2">
          <Link
            to="/category/0"
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              activeId === 0 ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-primary-100'
            }`}
          >
            全部
          </Link>
          {categories.map((c) => (
            <Link
              key={c.id}
              to={`/category/${c.id}`}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                activeId === c.id ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-primary-100'
              }`}
            >
              {c.name}
            </Link>
          ))}
        </div>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {filtered.map((m) => (
              <MangaCard key={m.id} manga={m} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300/70 bg-background-100 py-16 text-center">
            <i className="ri-file-search-line text-5xl text-foreground-300"></i>
            <p className="mt-4 text-sm text-foreground-600">没有找到相关作品，换个关键词试试吧</p>
            <Link to="/category/0" className="mt-4 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm text-background-50 hover:bg-primary-600 transition-colors">
              浏览全部漫画
            </Link>
          </div>
        )}
      </main>

      <Footer />
    </>
  );
}